import { useTheme } from '@mui/material';
import { Stats, useContextBridge } from '@react-three/drei';
import { Canvas } from '@react-three/fiber';
import { EffectComposer } from '@react-three/postprocessing';
import { Suspense, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Animation } from 'src/components/3DControls/Animation';
import { AnimCamera } from 'src/components/3DControls/CameraControls';
import { DefaultStudioLighting } from 'src/components/3DControls/DefaultStudioLighting';
import { Environment } from 'src/components/3DControls/Environment';
import { FocalPointRenderer } from 'src/components/3DControls/FocalPointRenderer';
import { HoverDetector } from 'src/components/3DControls/HoverDetector';
import { FadeModel, ModelVisibilityConfig } from 'src/components/3DControls/MaterialMutation/FadeModel';
import { N8AOEffect } from 'src/components/3DControls/N8AOEffect';
import { Feature } from 'src/Feature';
import { ProductContext } from 'src/providers/Product';
import { TenantContext, useTenant } from 'src/providers/Tenant';
import { UserContext } from 'src/providers/User';
import exposeAsGlobal from 'src/services/exposeAsGlobal';
import { PartContext } from 'src/services/part';
import { PartNavigationContext, usePartNavigation } from 'src/services/partNavigation';
import { PostProcessing } from 'src/services/productInventory';
import { AnimationClip, Box3, Object3D, Vector3, Vector3Tuple } from 'three';
import { meshesWithinObject } from '../3DControls/MaterialMutation/helpers';
import { OutlineEffect } from '../3DControls/OutlineEffect';
import Metrics from '../Metrics';
import ModelShadow from '../ModelShadow';
import { applySceneScaling, cameraIsStatic, getDefaultPosition, getModelSize, resetScene } from './utils';

interface IProps {
  scene: Object3D;
  clip: AnimationClip | undefined;
  fadeModel: ModelVisibilityConfig;
  highlight: string[];
  orbitCenter: Vector3Tuple | undefined;
  position: Vector3Tuple | undefined;
  setFullyRendered: (val: boolean) => void;
  isPartCatalogue: boolean;
  fullControls: boolean;
  modelBoundingBox?: Box3;
  postProcessingMode?: PostProcessing;
}

const SCALE_FACTOR = 4.51;

/* istanbul ignore next */
export function Scene({
  scene,
  clip,
  fadeModel,
  highlight,
  setFullyRendered,
  orbitCenter,
  position,
  isPartCatalogue,
  fullControls,
  modelBoundingBox,
  postProcessingMode,
}: IProps) {
  exposeAsGlobal('scene', scene);

  const ContextBridge = useContextBridge(
    UserContext,
    TenantContext,
    PartContext,
    PartNavigationContext,
    ProductContext
  );

  const history = useHistory();
  const { hotspots, parents } = usePartNavigation();
  const theme = useTheme();

  const { enabledFeatures } = useTenant();

  const applySvtScaling = applySceneScaling(scene, enabledFeatures);

  const zoom = applySvtScaling ? 1 : 3; // Change camera zoom on models after https://samsonvt.atlassian.net/browse/PHONG-157 to match focal length of Blender

  const { scale, centerOfAScene } = useMemo(() => {
    resetScene(scene); // make sure to reset the scene before any calculation involving the scene object
    const sizeBox = new Box3().setFromObject(scene);

    const scale = applySvtScaling ? SCALE_FACTOR / sizeBox.getSize(new Vector3()).length() : 1;
    const centerOfAScene = sizeBox.getCenter(new Vector3()).multiplyScalar(scale);

    return { scale, centerOfAScene };
  }, [scene, applySvtScaling]);

  const { modelSize, sceneMagnitude } = useMemo(() => getModelSize(modelBoundingBox), [modelBoundingBox]);

  const isResetAtRoot = parents.length === 0 && enabledFeatures.includes('configuration-management');

  const { defaultOrbitCenter, defaultPosition } = useMemo(
    () => ({
      defaultOrbitCenter: orbitCenter ?? centerOfAScene.toArray(),
      defaultPosition: getDefaultPosition(position, centerOfAScene, modelSize, enabledFeatures),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    cameraIsStatic(enabledFeatures)
      ? [enabledFeatures, centerOfAScene]
      : [centerOfAScene, orbitCenter, position, modelSize, enabledFeatures]
  );

  const [cameraHasMovedToNewPositionByProps, setCameraHasMovedToNewPositionByProps] = useState(false);

  const highlightedMeshes = useMemo(
    () =>
      highlight
        .map((name) => scene.getObjectByName(name))
        .filter((o) => o)
        .map((o) => meshesWithinObject(o!))
        .flat(),
    [highlight, scene] // Memoizing as scene.getObjectByName calls are expensive
  );
  // Outline effect mounting and unmounting is expensive, so we'd rather use it's enabled property.
  // Also we need this to adjust background's color space.
  const outlineEnabled = Boolean(highlightedMeshes.length);

  const postprocessingDisabled = enabledFeatures.includes('disable-postprocessing-effects');

  // Helper for highlight cypress test.
  exposeAsGlobal('highlight', highlight);

  return (
    <Canvas
      frameloop="demand"
      performance={{ min: 0.5 }}
      data-testid="three-js-canvas"
      gl={{
        preserveDrawingBuffer: !!(window as Window & { Cypress?: boolean }).Cypress, // For WebGL testing we need to make somescreenshots, and this is how we enable it.
      }}
      camera={{ zoom }}
      dpr={1}
    >
      <ContextBridge>
        <Metrics />
        <Suspense fallback={null}>
          <DefaultStudioLighting />
          <AnimCamera
            defaultOrbitCenter={defaultOrbitCenter}
            defaultPosition={defaultPosition}
            onCameraHasMovedToNewPositionByProps={setCameraHasMovedToNewPositionByProps}
            theme={theme}
            fullControls={fullControls}
            applySvtScaling={applySvtScaling}
            sceneMagnitude={sceneMagnitude}
            enabledFeatures={enabledFeatures}
            isResetAtRoot={isResetAtRoot}
          />
          {/* properties need to be attached to a group to avoid changing the scene directly */}
          <group scale={scale}>
            <primitive object={scene} light={false} />
          </group>

          <Feature flag="model-shadow">
            <ModelShadow
              scene={scene}
              scale={scale}
              shouldShowShadow={!(Boolean(clip) || Boolean(fadeModel.hide) || Boolean(fadeModel.show))}
              canvasBackground={theme.palette.canvasBackground.linear}
              postProcessingDisabled={postprocessingDisabled || postProcessingMode !== PostProcessing.ADVANCED}
            />
          </Feature>

          <Environment onCreated={() => setFullyRendered(true)} />
          {isPartCatalogue && hotspots.length === 0 && <HoverDetector model={scene} history={history} />}
          <Animation
            scene={scene}
            clip={clip}
            showPauseButton={!isPartCatalogue}
            cameraHasMovedToNewPositionByProps={cameraHasMovedToNewPositionByProps}
            theme={theme}
          />
          <FadeModel {...fadeModel} scene={scene} />
          {isPartCatalogue && <FocalPointRenderer history={history} hotspots={hotspots} theme={theme} />}
          {process.env.REACT_APP_DEV_SCENE_STATS_ENABLED === 'true' && <Stats />}
        </Suspense>
      </ContextBridge>

      <OutlineEffect selected={highlightedMeshes} enabled={outlineEnabled} />
      <EffectComposer enabled={!postprocessingDisabled && postProcessingMode === PostProcessing.ADVANCED}>
        <N8AOEffect quality="Ultra" halfRes screenSpaceRadius transparencyAware />
      </EffectComposer>
      <color
        attach="background"
        args={[
          // Adjust background color space when outline effect running. https://discourse.threejs.org/t/r3f-outline-issues/42533/5
          theme.palette.canvasBackground[outlineEnabled ? 'srgb' : 'linear'],
        ]}
      />
    </Canvas>
  );
}
